import { ui } from "@/components/ui";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { services as s } from "@/services";
import type { ActionViewT } from "@/types/database/actions";
import type { EntryViewT } from "@/types/database/entries";
import type { StatefulObservationViewT } from "@/types/database/observations";
import type { ExpandedSchedule } from "@/types/database/schedule";
import dayjs from "dayjs";
import { ArrowUpDown, Calendar, Contact2 } from "lucide-react";
import { FormInput } from "lucide-react";
import { useState } from "react";
import EntryCard from "../../entries/components/EntryCard";
import { sortEntryValuesView } from "../../entries/services/sortEntryValuesView";
import SchedulePreview from "../../schedules/components/SchedulePreview";
import EditObservation from "./EditObservation";
import ObservationEntryValuesCard from "./ObservationEntryValuesCard";

// Define a type for the form object
interface FormObject {
	form_name: string;
	form_description: string;
}

interface ObservationInHistoryProps {
	observation: StatefulObservationViewT;
	index: number;
	actions: ActionViewT[];
	sortAscending: boolean;
	setSortAscending: (value: boolean) => void;
}

const ObservationInHistory = ({
	observation,
	index,
	sortAscending,
	setSortAscending,
}: ObservationInHistoryProps) => {
	const [isEditingObservation, setIsEditingObservation] = useState(false);
	const hasEntries =
		observation.entries_view && observation.entries_view.length > 0;

	return (
		<div
			key={observation.observation_id}
			className="space-y-2 dark:bg-slate-900 shadow-sm px-4 py-4 rounded-lg w-full observation-card"
		>
			{/* Observation Header */}
			<div className="mb-4">
				<div className="flex flex-row justify-between items-start w-full">
					<h3 className="mb-2 font-semibold text-xl">
						{observation.observation_name ?? `Observation #${index + 1}`}
					</h3>
					<Button
						onClick={() => setIsEditingObservation(!isEditingObservation)}
						variant="link"
						className="my-0 px-2 py-0 w-fit h-fit"
					>
						{isEditingObservation ? "Done" : "Edit"}
					</Button>
				</div>
				{isEditingObservation ? (
					<EditObservation observation={observation} />
				) : (
					<p className="flex items-center gap-2 text-muted-foreground text-sm">
						{s.time.formatDate(observation.start_date)} -{" "}
						{observation.end_date === null
							? "Now"
							: s.time.formatDate(observation.end_date)}
					</p>
				)}
			</div>
			<Separator />
			<div className="">
				{hasEntries ? (
					<>
						<Accordion collapsible defaultValue="entries" type="single">
							<AccordionItem value="entries">
								<AccordionTrigger className="text-sm">
									<span>See Observation History</span>
								</AccordionTrigger>
								<AccordionContent className="space-y-2">
									{observation.entries_view
										.map((entry) => ({
											...entry,
											entry_values: sortEntryValuesView(entry.entry_values),
										}))
										.sort((a, b) =>
											sortAscending
												? dayjs(a.calculated_entry_time).diff(
														dayjs(b.calculated_entry_time),
													)
												: dayjs(b.calculated_entry_time).diff(
														dayjs(a.calculated_entry_time),
													),
										)
										.map((entry) => (
											<ObservationEntryValuesCard
												editable={true}
												key={entry.entry_id}
												entry={entry as EntryViewT}
												size="sm"
											/>
										))}
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</>
				) : (
					<div className="flex flex-col justify-center items-center py-4 text-muted-foreground">
						<p className="text-sm">No entries yet</p>
					</div>
				)}
			</div>
			<Accordion collapsible type="single">
				<AccordionItem value="observation-details">
					<AccordionTrigger className="text-sm">
						Show More Details
					</AccordionTrigger>
					<AccordionContent className="space-y-2 pt-2">
						{/* Form Details */}
						{observation.form && (
							<div className="space-y-2 mb-4">
								<div className="flex items-center gap-2 font-medium text-slate-600 text-sm">
									<FormInput size={20} />
									Forms
								</div>
								<div>
									<p className="text-md text-slate-800">
										{(observation.form as unknown as FormObject).form_name}
									</p>
									<p className="text-slate-500 text-sm">
										{
											(observation.form as unknown as FormObject)
												.form_description
										}
									</p>
								</div>
							</div>
						)}

						{/* Schedules */}
						<Separator />
						<div className="flex items-center gap-2 font-medium text-slate-600 text-sm">
							<Calendar size={20} />
							Monitoring schedule
						</div>
						<SchedulePreview
							title="Monitoring schedule"
							schedule={observation.schedules as ExpandedSchedule}
						/>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	);
};

export default ObservationInHistory;
